import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ConfigContext } from '../shared/ConfigContext';
import { useEntitlements } from '../../contexts/EntitlementsContext';
import { usePlanLimitContent } from '../help/planLimitContent';

const PAGE_SIZE = 25;
const EMPTY_BILLING = { companyName: '', addressLine1: '', city: '', state: '', postalCode: '', country: '' };
const EMPTY_FORM = { username: '', firstName: '', lastName: '', email: '', role: 'user', billing: EMPTY_BILLING };
const EMPTY_EXISTING_FORM = { email: '', role: 'user' };

// Same accumulating "load more" fetch pattern as useCategoriesPage. Create
// and edit (drawerMode, mirroring useCategoriesPage) plus single/bulk
// delete, reusing DataTableCard's bulkBar. The create drawer additionally
// has inviteMode 'existing': attach an account that already exists (in any
// company) through a Membership instead of creating a new User.
const useUsersPage = () => {
  const { t } = useTranslation();
  const config = useContext(ConfigContext);
  const [searchParams] = useSearchParams();
  const previewState = searchParams.get('previewState');

  const [companyId] = useState(() => localStorage.getItem('companyId') || '');
  // A plan that carries the users.admin_only_seats restriction (the
  // invoice-only plan) sells admin-only seats: every invite is an admin and
  // the User role is never offered. Every plan without that row keeps both
  // roles. The server refuses a User invite anyway (402); this only keeps
  // the form from proposing it.
  const { usage } = useEntitlements();
  const userRoleAllowed = usage('users.admin_only_seats')?.enabled !== true;
  const defaultRole = userRoleAllowed ? 'user' : 'admin';
  const planLimit = usePlanLimitContent('users.seats');

  const [rows, setRows] = useState([]);
  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorInfo, setErrorInfo] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [selection, setSelection] = useState([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('create'); // 'create' | 'edit'
  const [inviteMode, setInviteMode] = useState('create'); // 'create' | 'existing' (create drawer only)
  const [form, setForm] = useState(EMPTY_FORM);
  const [existingForm, setExistingForm] = useState(EMPTY_EXISTING_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  // Full shippingAddress of the user being edited — the drawer only exposes
  // the billing subset, so unshown fields (phone, addressLine2, …) are
  // merged back into the save payload instead of being wiped.
  const editingShippingRef = useRef(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargets, setConfirmTargets] = useState([]);
  const [deleting, setDeleting] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const fetchUsers = useCallback((reset = true) => {
    if (!config?.backendUrl || !companyId) return;
    if (reset) setStatus('loading'); else setLoadingMore(true);
    const page = reset ? 1 : Math.floor(rowsRef.current.length / PAGE_SIZE) + 1;
    const params = { companyId, page, pageSize: PAGE_SIZE };
    if (search) params.search = search;
    axios.get(`${config.backendUrl}/users`, { params })
      .then((res) => {
        setRows((prev) => (reset ? res.data.items : [...prev, ...res.data.items]));
        setTotal(res.data.total);
        setStatus('ready');
      })
      .catch((error) => {
        console.error('Error fetching users', error);
        if (error.response?.status === 403 || error.response?.status === 401) {
          setStatus('permission');
        } else {
          setErrorInfo({
            request: `GET /users · ${error.response?.status || t('N/A')} · ${new Date().toLocaleTimeString()}`,
          });
          setStatus('error');
        }
      })
      .finally(() => setLoadingMore(false));
  }, [config, companyId, search, t]);

  useEffect(() => { fetchUsers(true); }, [fetchUsers]);

  const loadMore = () => fetchUsers(false);
  const remaining = Math.max(0, total - rows.length);
  const nextBatchSize = Math.min(PAGE_SIZE, remaining);

  // ---- Invite / edit drawer ------------------------------------------------//
  const openInviteDrawer = () => {
    setDrawerMode('create');
    setInviteMode('create');
    setForm({ ...EMPTY_FORM, role: defaultRole });
    setExistingForm({ ...EMPTY_EXISTING_FORM, role: defaultRole });
    setFormErrors({});
    setEditingId(null);
    setDrawerOpen(true);
  };

  const openEditDrawer = (user) => {
    setDrawerMode('edit');
    editingShippingRef.current = user.shippingAddress || null;
    setForm({
      username: user.username || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      role: user.role || 'user',
      billing: Object.keys(EMPTY_BILLING).reduce((acc, key) => {
        acc[key] = user.shippingAddress?.[key] ?? '';
        return acc;
      }, {}),
    });
    setFormErrors({});
    setEditingId(user._id);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    if (saving) return;
    setDrawerOpen(false);
  };

  // The entitlement snapshot can land after the invite drawer opened with
  // the 'user' default — snap the pending invite back to admin. Edit mode
  // is left alone: an existing member's role isn't ours to change here.
  useEffect(() => {
    if (userRoleAllowed) return;
    if (drawerMode === 'create') setForm((f) => (f.role === 'user' ? { ...f, role: 'admin' } : f));
    setExistingForm((f) => (f.role === 'user' ? { ...f, role: 'admin' } : f));
  }, [userRoleAllowed, drawerMode]);

  // Deep link from the invoice dialog's "Bill to" pencil: /users?editUser=<id>
  // opens that user's edit drawer once. Fetched by id because the user may
  // not be on the first page of results.
  const editUserParam = searchParams.get('editUser');
  const editParamHandledRef = useRef(false);
  useEffect(() => {
    if (!config?.backendUrl || !editUserParam || editParamHandledRef.current) return;
    editParamHandledRef.current = true;
    axios.get(`${config.backendUrl}/users/${editUserParam}`)
      .then((res) => openEditDrawer(res.data))
      .catch((error) => {
        console.error('Error loading user for edit', error);
        notify(t('Error loading user.', { defaultValue: 'Error loading user.' }), 'error');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, editUserParam]);

  // Create a new User (drawerMode 'create') or update one (drawerMode 'edit').
  const handleSaveUser = async () => {
    const errors = {};
    if (!form.username.trim()) errors.username = t('Username is required');
    if (!form.firstName.trim()) errors.firstName = t('First name is required', { defaultValue: 'First name is required' });
    if (!form.lastName.trim()) errors.lastName = t('Last name is required', { defaultValue: 'Last name is required' });
    if (!form.email.trim()) errors.email = t('Email is required', { defaultValue: 'Email is required' });
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setSaving(true);
    try {
      const payload = {
        username: form.username,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        role: form.role,
      };
      if (drawerMode === 'create') {
        await axios.post(`${config.backendUrl}/users`, { ...payload, companyId });
        notify(t('User successfully added!'));
      } else {
        payload.shippingAddress = { ...(editingShippingRef.current || {}), ...form.billing };
        await axios.put(`${config.backendUrl}/users/${editingId}`, payload);
        notify(t('User successfully updated!', { defaultValue: 'User successfully updated!' }));
      }
      setDrawerOpen(false);
      fetchUsers(true);
    } catch (error) {
      console.error('Error saving user', error);
      notify(error.response?.data?.error || (drawerMode === 'create' ? t('Error creating user.') : t('Error updating user.', { defaultValue: 'Error updating user.' })), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveExisting = async () => {
    if (!existingForm.email.trim()) {
      setFormErrors({ email: t('Email is required', { defaultValue: 'Email is required' }) });
      return;
    }
    setFormErrors({});
    setSaving(true);
    try {
      await axios.post(`${config.backendUrl}/companies/${companyId}/members`, {
        email: existingForm.email,
        role: existingForm.role,
      });
      notify(t('User successfully added!'));
      setDrawerOpen(false);
      fetchUsers(true);
    } catch (error) {
      console.error('Error adding existing user', error);
      notify(error.response?.data?.error || t('Error adding user.', { defaultValue: 'Error adding user.' }), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => {
    if (saving) return;
    return (drawerMode === 'create' && inviteMode === 'existing') ? handleSaveExisting() : handleSaveUser();
  };

  // ---- Delete confirm (single row or bulk selection) ----------------------//
  const openDeleteConfirm = (targets) => {
    setConfirmTargets(targets);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (confirmTargets.length === 0) return;
    setDeleting(true);
    try {
      await Promise.all(confirmTargets.map((u) => axios.delete(`${config.backendUrl}/users/${u._id}`)));
      notify(t('User(s) successfully removed!'));
      setConfirmOpen(false);
      setSelection([]);
      fetchUsers(true);
    } catch (error) {
      console.error('Error removing users', error);
      notify(t('Error removing users.'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const mappedRows = rows.map((user) => ({ ...user, id: user._id }));
  const selectedUsers = mappedRows.filter((u) => selection.includes(u._id));

  const isPreviewEmptyKind = previewState === 'empty' || previewState === 'no-match';
  const effectiveStatus = isPreviewEmptyKind ? 'ready' : (previewState || status);
  const effectiveRows = isPreviewEmptyKind ? [] : (effectiveStatus === 'ready' ? mappedRows : []);
  const isNoMatch = isPreviewEmptyKind ? previewState === 'no-match' : Boolean(search);

  return {
    t, companyId, total, errorInfo, effectiveStatus, effectiveRows, isNoMatch, search,
    searchInput, setSearchInput, setSearch,
    loadingMore, loadMore, remaining, nextBatchSize, fetchUsers,
    selection, setSelection, selectedUsers,
    drawerOpen, drawerMode, inviteMode, setInviteMode, form, setForm, existingForm, setExistingForm, userRoleAllowed,
    formErrors, saving, closeDrawer, openInviteDrawer, openEditDrawer, handleSave,
    confirmOpen, setConfirmOpen, confirmTargets, deleting, openDeleteConfirm, handleConfirmDelete,
    snackbar, setSnackbar,
    planLimit,
  };
};

export default useUsersPage;
